import { __ } from "@wordpress/i18n";
import { Info } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
} from "../ui/index";

const POSITIONS = [
    { value: "right", label: __("Right", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "left", label: __("Left", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "top", label: __("Top", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "bottom", label: __("Bottom", "wpbean-product-gallery-slider-for-woocommerce") },
];

const SIZE_MODES = [
    { value: "auto",   label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "custom", label: __("Custom", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "match",  label: __("Match Image", "wpbean-product-gallery-slider-for-woocommerce") },
];

const CURSOR_SIZE_MODES = [
    { value: "auto",   label: __("Auto", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "custom", label: __("Custom", "wpbean-product-gallery-slider-for-woocommerce") },
];

export default function ZoomWindowPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    if (settings.zoomType !== "window") {
        return (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-10 text-center">
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50">
                    <Info className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-sm font-semibold text-gray-800">
                    {__("Zoom Window is inactive", "wpbean-product-gallery-slider-for-woocommerce")}
                </p>
                <p className="mt-1.5 text-sm text-gray-400">
                    {__("Go to the", "wpbean-product-gallery-slider-for-woocommerce")}{" "}
                    <button
                        type="button"
                        onClick={() => actions.setTab("zoom")}
                        className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                    >
                        {__("Zoom tab", "wpbean-product-gallery-slider-for-woocommerce")}
                    </button>{" "}
                    {__("and set the Display Mode to", "wpbean-product-gallery-slider-for-woocommerce")}{" "}
                    <strong className="text-gray-600">{__("Window", "wpbean-product-gallery-slider-for-woocommerce")}</strong>.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("Window Size", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Dimensions of the floating zoom panel.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Size Mode", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Auto fits the available space while preserving image aspect ratio. Match Image mirrors the main image exactly. Custom uses your defined dimensions.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <ButtonGroup
                        options={SIZE_MODES}
                        value={settings.zoomWindowSizeMode}
                        onChange={(v) => s("zoomWindowSizeMode", v)}
                    />
                </SettingRow>

                {settings.zoomWindowSizeMode === "custom" && (
                    <>
                        <SettingRow
                            label={__("Width", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Width of the zoom window in pixels.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.zoomWindowWidth}
                                onChange={(v) => s("zoomWindowWidth", v)}
                                min={100}
                                max={1200}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Height", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Height of the zoom window in pixels.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.zoomWindowHeight}
                                onChange={(v) => s("zoomWindowHeight", v)}
                                min={100}
                                max={1200}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title={__("Position", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Where the zoom window appears relative to the main image.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow label={__("Placement", "wpbean-product-gallery-slider-for-woocommerce")}>
                    <ButtonGroup
                        options={POSITIONS}
                        value={settings.zoomWindowPosition}
                        onChange={(v) => s("zoomWindowPosition", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Horizontal Offset", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Extra space between the image edge and the window.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.zoomWindowOffsetX}
                        onChange={(v) => s("zoomWindowOffsetX", v)}
                        min={-100}
                        max={200}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label={__("Vertical Offset", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Extra vertical space between the image edge and the window.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.zoomWindowOffsetY}
                        onChange={(v) => s("zoomWindowOffsetY", v)}
                        min={-100}
                        max={200}
                        unit="px"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Fade Easing", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Smooth the zoom window appearing and disappearing.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Enable Fade", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Fade the zoom window in and out instead of snapping.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.zoomWindowFadeEasing}
                        onChange={(v) => s("zoomWindowFadeEasing", v)}
                    />
                </SettingRow>

                {settings.zoomWindowFadeEasing && (
                    <SettingRow
                        label={__("Fade Duration", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("How long the fade takes. Higher values are slower.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <NumberInput
                            value={settings.zoomWindowFadeEasingAmount}
                            onChange={(v) => s("zoomWindowFadeEasingAmount", v)}
                            min={50}
                            max={2000}
                            unit="ms"
                            width="w-24"
                        />
                    </SettingRow>
                )}
            </SectionCard>

            <SectionCard
                title={__("Window Style", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Visual appearance of the zoom window panel.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Border Radius", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Rounded corner radius of the window.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.zoomWindowBorderRadius}
                        onChange={(v) => s("zoomWindowBorderRadius", v)}
                        min={0}
                        max={50}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow label={__("Border Color", "wpbean-product-gallery-slider-for-woocommerce")}>
                    <ColorInput
                        value={settings.zoomWindowBorderColor}
                        onChange={(v) => s("zoomWindowBorderColor", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Border Width", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Thickness of the window border.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.zoomWindowBorderWidth}
                        onChange={(v) => s("zoomWindowBorderWidth", v)}
                        min={0}
                        max={10}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label={__("Drop Shadow", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Apply a soft box shadow around the zoom window.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.zoomWindowShadow}
                        onChange={(v) => s("zoomWindowShadow", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Cursor Overlay", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Selection-box indicator drawn on the main image to show what area the zoom window is displaying.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Show Cursor", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Display a selection box on the image while zooming.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.zoomWindowCursor}
                        onChange={(v) => s("zoomWindowCursor", v)}
                    />
                </SettingRow>

                {settings.zoomWindowCursor && (
                    <>
                        <SettingRow
                            label={__("Size Mode", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Auto accurately represents the visible zoom area. Custom lets you set any fixed size.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <ButtonGroup
                                options={CURSOR_SIZE_MODES}
                                value={settings.zoomWindowCursorSizeMode}
                                onChange={(v) => s("zoomWindowCursorSizeMode", v)}
                            />
                        </SettingRow>

                        {settings.zoomWindowCursorSizeMode === "custom" && (
                            <>
                                <SettingRow
                                    label={__("Width", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Fixed width of the cursor selection box.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <NumberInput
                                        value={settings.zoomWindowCursorWidth}
                                        onChange={(v) => s("zoomWindowCursorWidth", v)}
                                        min={10}
                                        max={600}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Height", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Fixed height of the cursor selection box.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <NumberInput
                                        value={settings.zoomWindowCursorHeight}
                                        onChange={(v) => s("zoomWindowCursorHeight", v)}
                                        min={10}
                                        max={600}
                                        unit="px"
                                        width="w-24"
                                    />
                                </SettingRow>
                            </>
                        )}

                        <SettingRow
                            label={__("Border Radius", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Rounded corner radius of the cursor box. Use 50% of its size for a circle.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.zoomWindowCursorBorderRadius}
                                onChange={(v) => s("zoomWindowCursorBorderRadius", v)}
                                min={0}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Background", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Fill color inside the selection box. Use a semi-transparent color.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <ColorInput
                                value={settings.zoomWindowCursorBg}
                                onChange={(v) => s("zoomWindowCursorBg", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Border Color", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Color of the selection box border.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <ColorInput
                                value={settings.zoomWindowCursorBorderColor}
                                onChange={(v) => s("zoomWindowCursorBorderColor", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Border Width", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Thickness of the selection box border.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.zoomWindowCursorBorderWidth}
                                onChange={(v) => s("zoomWindowCursorBorderWidth", v)}
                                min={0}
                                max={8}
                                unit="px"
                            />
                        </SettingRow>

                    </>
                )}
            </SectionCard>
        </div>
    );
}
